import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type {
  StoryDetailResponse,
  StoryListRequest,
  StorySimpleResponse,
  SymbolStoryListRequest,
} from '@/lib/services/newstory.service';
import {
  fetchStoryDetailById,
  fetchStoryList,
  fetchStorySimple,
  fetchSymbolStoryList,
} from '@/lib/services/newstory.service';

/**
 * Hook to fetch story detail by ID using React Query
 * @param storyId - The unique identifier for the story
 * @returns Query result with story detail data, loading state, and error handling
 */
export function useStoryDetail(storyId: string | undefined) {
  return useQuery<StoryDetailResponse>({
    queryKey: ['storyDetail', storyId],
    queryFn: async () => {
      if (!storyId) throw new Error('Story ID is required');
      return await fetchStoryDetailById(storyId);
    },
    enabled: !!storyId, // Only run query if storyId is provided
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch simple story data for a trader address using React Query
 * @param address - The trader's address
 * @returns Query result with simple story data, loading state, and error handling
 */
export function useStorySimple(address: string | undefined) {
  return useQuery<StorySimpleResponse>({
    queryKey: ['storySimple', address],
    queryFn: async () => {
      if (!address) throw new Error('Address is required');
      return await fetchStorySimple(address);
    },
    enabled: !!address, // Only run query if address is provided
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch paginated story list for a trader address and symbol using useInfiniteQuery
 * @param address - The trader's address
 * @param symbol - The trading symbol
 * @param params - Additional query parameters (excluding limit and offset which are managed by pagination)
 * @param initialLimit - Initial number of items to load
 * @returns Infinite query result with story list data, loading state, and pagination controls
 */
export function useInfiniteStoryList(
  address: string | undefined,
  symbol: string | undefined,
  options: {
    params?: Omit<StoryListRequest, 'limit' | 'offset'>;
    initialLimit?: number;
  } = {},
) {
  const { params = {}, initialLimit = 10 } = options;
  return useInfiniteQuery({
    queryKey: ['infiniteStoryList', address, symbol, params, initialLimit],
    queryFn: async ({ pageParam = 0 }) => {
      if (!address || !symbol) {
        throw new Error('Address and symbol are required');
      }
      const result = await fetchStoryList(address, symbol, {
        ...params,
        limit: initialLimit,
        offset: pageParam,
      });
      return {
        ...result,
        nextOffset:
          result.list.length === initialLimit
            ? pageParam + initialLimit
            : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!address && !!symbol, // Only run query if both address and symbol are provided
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    initialPageParam: 0,
  });
}

/**
 * Hook to fetch paginated story list for a specific symbol using useInfiniteQuery
 * @param symbol - The trading symbol
 * @param params - Additional query parameters (excluding limit and offset which are managed by pagination)
 * @param initialLimit - Initial number of items to load
 * @returns Infinite query result with symbol story list data, loading state, and pagination controls
 */
export function useInfiniteSymbolStoryList(
  symbol: string | undefined,
  params: Omit<SymbolStoryListRequest, 'limit' | 'offset'> = {},
  initialLimit = 10,
) {
  return useInfiniteQuery({
    queryKey: ['infiniteSymbolStoryList', symbol, params, initialLimit],
    queryFn: async ({ pageParam = 0 }) => {
      if (!symbol) {
        throw new Error('Symbol is required');
      }
      const result = await fetchSymbolStoryList(symbol, {
        ...params,
        limit: initialLimit,
        offset: pageParam,
      });
      return {
        ...result,
        nextOffset:
          result.list.length === initialLimit
            ? pageParam + initialLimit
            : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!symbol, // Only run query if symbol is provided
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    initialPageParam: 0,
  });
}
